import React, { useState, useEffect, useCallback } from "react";
import { GTVMessage, PlayerType } from "../../shared/util/types";
import Instructions from "../../shared/components/Instructions";
import ShowCards from "../../shared/components/ShowCards";
import { Box } from "@mantine/core";
import { DEFAULT_THEME } from "../../shared/util/consts";

type CAHGameProps = {
  sendHostMessage: (msg: string) => Promise<void>;
  gameMessage: GTVMessage | null;
  playMedia: (mediaUrl: string) => void;
};

const CAHGame = ({ sendHostMessage, gameMessage, playMedia }: CAHGameProps) => {
  const [players, setPlayers] = useState<PlayerType[]>([]);
  const theme = DEFAULT_THEME;
  const [payload, setPayload] = useState<any | undefined>(undefined);
  const [state, setState] = useState("NextRound"); // Should be NextRound to start
  const [cards, setCards] = useState<string[]>([]);
  const [round, setRound] = useState(1);
  const [instructions, setInstructions] = useState("");
  const [reminder, setReminder] = useState("");
  const [judge, setJudge] = useState("");
  const [responseMessage, setResponseMessage] = useState("");

  const sendResponseMessage = useCallback(
    async (message: string) => {
      if (message.length > 0) {
        await sendHostMessage(message);
      }
    },
    [sendHostMessage],
  );

  useEffect(() => {
    if (payload) {
      payload.Judge && setJudge(payload.Judge);
      payload.Round && setRound(payload.Round);
      payload.Instructions && setInstructions(payload.Instructions);
      payload.Reminder && setReminder(payload.Reminder);
      if (payload.ResponseMessage) {
        setResponseMessage(payload.ResponseMessage);
        sendResponseMessage(payload.ResponseMessage);
      }
    }
  }, [payload, sendResponseMessage]);

  const handleMessage = useCallback(
    async (msg: GTVMessage) => {
      console.log("CAH Processing msg", msg);
      if (!msg) {
        return false;
      }
      setPayload(msg.Payload);
      switch (msg.Type) {
        case "AddPlayer":
          const newPlayer = msg.Payload as PlayerType;
          setPlayers([...players, { ...newPlayer, Submitted: true }]);
          break;
        case "NextRound":
          setState(msg.Type);
          setPlayers(
            msg.Payload.Players.map((p: any) => {
              return {
                Name: p.Player,
                Score: p.Score,
                Submitted: p.Player !== msg.Payload.Judge,
              };
            }),
          );
          const hasLastWinner = msg.Payload.LastWinningCard.length > 0;
          setCards(hasLastWinner ? [msg.Payload.LastWinningCard] : []);
          break;
        case "NewBlackCard":
          setState(msg.Type);
          setPlayers(
            msg.Payload.Players.map((p: any) => {
              return {
                Name: p.Player,
                Score: p.Score,
                Submitted: p.Player === msg.Payload.Judge,
              };
            }),
          );
          setCards([]);
          break;
        case "OnePlayerIn":
          playMedia("/drop2.mp3");
          setPlayers(
            players.map((p) => {
              return {
                ...p,
                Submitted:
                  p.Name === (msg.Payload as string) ? true : p.Submitted,
              };
            }),
          );
          break;
        case "AllPlayersIn":
          setState(msg.Type);
          setPlayers(
            msg.Payload.Players.map((p: any) => ({
              Name: p.Player,
              Score: p.Score,
              Submitted: true,
            })),
          );
          setCards(msg.Payload.Submissions);
          break;
        case "UpdateScores":
          let playersToUpdate = msg.Payload as PlayerType[];
          setPlayers(
            players.map((p) => {
              const updatedPlayer = playersToUpdate.find(
                (up) => up.Name === p.Name,
              );
              return updatedPlayer
                ? {
                    ...p,
                    Score: updatedPlayer.Score,
                    Submitted: updatedPlayer.Submitted,
                  }
                : p;
            }),
          );
          break;
        default:
          console.log("Unimplemented message:", msg);
          break;
      }
      return false;
    },
    [players, setPlayers, playMedia],
  );
  useEffect(() => {
    gameMessage && handleMessage(gameMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameMessage]);
  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "calc(100vh)",
        justifyContent: "space-between",
      }}
    >
      {(round <= 1 && state === "NextRound") || state === "NewBlackCard" ? (
        <Instructions
          theme={theme}
          judge={judge}
          instructions={instructions}
          players={players}
          reminder={reminder}
          round={"Round " + round}
          onRenderComplete={async () => {
            // TODO this doesn't get called or calls with empty message.
            await sendHostMessage(responseMessage);
          }}
        />
      ) : (
        <ShowCards
          theme={theme}
          judge={judge}
          instructions={instructions}
          players={players}
          cards={cards}
          reminder={reminder}
          round={"Round " + round}
          onRenderComplete={async () => {
            // TODO this doesn't get called or calls with empty message.
            await sendHostMessage(responseMessage);
          }}
        />
      )}
    </Box>
  );
};

export default CAHGame;
